<template>
  <div
    ref="paneViewLayoutRef"
    class="direct-prr-tree-view"
    :class="{ 'direct-prr-tree-view--split': showDetailPanel }"
    data-testid="direct-prr-tree-view"
  >
    <div
      class="direct-prr-tree-view__tree"
      :class="{
        'direct-prr-tree-view__tree--has-content': !isEmpty,
        'direct-prr-tree-view__tree--empty': isEmpty,
      }"
      :style="leftPaneStyle"
      data-testid="direct-prr-tree-pane"
    >
      <div class="direct-prr-tree-view__tree-scroll">
        <div
          v-if="isEmpty && loading"
          class="tree-skeleton-container"
          data-testid="direct-prr-tree-loading"
        >
          <div
            v-for="n in 5"
            :key="'skeleton-loading-' + n"
            class="tree-skeleton-item"
          >
            <div class="tree-skeleton-icon" />
            <div class="tree-skeleton-content">
              <div class="tree-skeleton-title" />
            </div>
          </div>
        </div>

        <div
          v-else-if="isEmpty"
          class="empty-tree-state"
          data-testid="direct-prr-tree-empty"
        >
          <p>{{ emptyMessage }}</p>
        </div>

        <template v-else>
          <TreeItem
            v-for="(item, index) in treeData"
            :key="`${item.type}-${item.id}`"
            :item="item"
            :expanded-items="expandedItems"
            :selected-item="resolvedSelected"
            :is-last-sibling="index === treeData.length - 1"
            :has-next-same-type="hasNextSameType(treeData, index)"
            @toggle="handleToggle"
            @item-selected="handleItemSelected"
          />
        </template>
      </div>
    </div>

    <div
      v-if="showDetailPanel"
      class="direct-prr-tree-view__resize"
      data-testid="direct-prr-tree-resize"
      role="separator"
      aria-label="Resize panels"
      @mousedown="handlePaneResizeStart"
    />

    <div
      v-if="showDetailPanel"
      ref="paneViewRightRef"
      class="direct-prr-tree-view__detail"
      data-testid="direct-prr-tree-detail"
    >
      <slot name="detail" :selected="resolvedSelected" :item="selectedTreeItem">
        <RoleCard
          v-if="selectedIsRole && resolvedSelected && resolvedSelected.id != null"
          :key="`role-${resolvedSelected.id}`"
          :role-id="resolvedSelected.id"
          :name="selectedLabel.name"
          :code="selectedLabel.code"
          :status="selectedStatus"
          :drilldown-in-panel="true"
          :breadcrumbs-list="[]"
          data-testid="role-detail-panel"
          @drilldown="handleCardDrilldown"
          @item-deleted="handleItemDeleted"
          @roleDataUpdated="handleRoleDataUpdated"
        />
        <ResponsibilityCard
          v-else-if="selectedIsResponsibility && resolvedSelected && resolvedSelected.id != null"
          :key="`responsibility-${resolvedSelected.id}`"
          :res-id="resolvedSelected.id"
          :name="selectedLabel.name"
          :code="selectedLabel.code"
          :status="selectedStatus"
          :drilldown-in-panel="true"
          data-testid="resp-detail-panel"
          @drilldown="handleCardDrilldown"
          @item-deleted="handleItemDeleted"
          @responsibilityDataUpdated="handleResponsibilityDataUpdated"
        />
        <EmptyState
          v-else-if="deletedItem"
          icon="alert-circle"
          :title="
            deletedItem.type === 'role'
              ? 'Role successfully deleted'
              : 'Responsibility successfully deleted'
          "
          supportingText="Select a new branch in the tree"
        />
        <div v-else class="direct-prr-detail-empty" data-testid="direct-prr-tree-detail-empty">
          <p>Select a role or responsibility to view details</p>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import Vue, { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import TreeItem from "@components/PositionsTreeNavigator/TreeItem.vue";
import RoleCard from "@components/RoleCard/RoleCard.vue";
import ResponsibilityCard from "@components/ResponsibilityCard/ResponsibilityCard.vue";
import EmptyState from "@components/EmptyState/EmptyState.vue";

/**
 * Position-detail navigator: tree starts at roles and responsibilities
 * (the position is already the page, so it is never shown). Nested
 * responsibilities stay under their role. The right pane is the same
 * RoleCard / ResponsibilityCard used in Organization Positions.
 */
const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  /** Single position node; its children become the tree roots when `data` is empty. */
  position: {
    type: Object,
    default: null,
  },
  selectedItem: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  emptyMessage: {
    type: String,
    default: "No roles or responsibilities attached",
  },
  showDetailPanel: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "item-selected",
  "toggle-expand",
  "drilldown",
  "item-deleted",
  "role-data-updated",
  "responsibility-data-updated",
]);

const expandedItems = reactive({});
const selectedItemInternal = ref(null);
/** ER-11676: a role/responsibility was deleted from the detail pane — show the shared EmptyState instead of navigating away. */
const deletedItem = ref(null);
const paneViewLayoutRef = ref(null);
const paneViewRightRef = ref(null);
const paneLeftWidthPercent = ref(33);
const paneViewRightHeight = ref(null);
let paneViewRightResizeObserver = null;

const MIN_LEFT_PANE_PERCENT = 25;
const MAX_LEFT_PANE_PERCENT = 33;

const leftPaneStyle = computed(() => {
  if (!props.showDetailPanel) return {};
  const style = { width: `${paneLeftWidthPercent.value}%` };
  if (paneViewRightHeight.value != null) {
    style.height = `${paneViewRightHeight.value}px`;
    style.maxHeight = `${paneViewRightHeight.value}px`;
  }
  return style;
});

function handlePaneResizeStart(e) {
  if (e.button !== 0) return;
  e.preventDefault();
  const layoutEl = paneViewLayoutRef.value;
  if (!layoutEl) return;
  const onMove = (moveEvent) => {
    const rect = layoutEl.getBoundingClientRect();
    const x = moveEvent.clientX - rect.left;
    const percent = Math.round((x / rect.width) * 100);
    paneLeftWidthPercent.value = Math.min(
      MAX_LEFT_PANE_PERCENT,
      Math.max(MIN_LEFT_PANE_PERCENT, percent)
    );
  };
  const onUp = () => {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
}

function syncRightPaneHeight() {
  const el = paneViewRightRef.value;
  if (!el) return;
  paneViewRightHeight.value = el.getBoundingClientRect().height;
}

function attachRightPaneHeightObserver() {
  const el = paneViewRightRef.value;
  if (!el || paneViewRightResizeObserver || typeof ResizeObserver === "undefined") return;
  syncRightPaneHeight();
  paneViewRightResizeObserver = new ResizeObserver(() => {
    syncRightPaneHeight();
  });
  paneViewRightResizeObserver.observe(el);
}

function detachRightPaneHeightObserver() {
  if (paneViewRightResizeObserver) {
    paneViewRightResizeObserver.disconnect();
    paneViewRightResizeObserver = null;
  }
}

function flattenGroups(items) {
  if (!Array.isArray(items)) return [];
  const out = [];
  items.forEach((item) => {
    if (!item) return;
    if (item.type === "group") {
      out.push(...flattenGroups(item.children || []));
      return;
    }
    out.push({
      ...item,
      children: flattenGroups(item.children || []),
    });
  });
  return out;
}

/** Drop position nodes — this tree is used on a position detail page. */
function hoistPositionNodes(items) {
  const flattened = flattenGroups(items);
  const roots = [];
  flattened.forEach((item) => {
    if (item.type === "position") {
      roots.push(...hoistPositionNodes(item.children || []));
      return;
    }
    roots.push({
      ...item,
      children: hoistPositionNodes(item.children || []),
    });
  });
  return roots;
}

const treeData = computed(() => {
  const source =
    Array.isArray(props.data) && props.data.length > 0
      ? props.data
      : props.position
        ? [props.position]
        : [];
  return hoistPositionNodes(source);
});

const isEmpty = computed(() => treeData.value.length === 0);

const resolvedSelected = computed(() => {
  if (deletedItem.value) return null;
  if (props.selectedItem && props.selectedItem.id != null) {
    return props.selectedItem;
  }
  return selectedItemInternal.value;
});

const selectedTreeItem = computed(() => {
  const selected = resolvedSelected.value;
  if (!selected || selected.id == null) return null;
  return findItemById(selected.id, treeData.value);
});

const selectedIsRole = computed(() => {
  const type = (resolvedSelected.value?.type || selectedTreeItem.value?.type || "").toLowerCase();
  return type === "role";
});

const selectedIsResponsibility = computed(() => {
  const type = (resolvedSelected.value?.type || selectedTreeItem.value?.type || "").toLowerCase();
  return type === "responsibility";
});

const selectedLabel = computed(() => parseCodeAndName(selectedTreeItem.value || resolvedSelected.value));

const selectedStatus = computed(() => {
  const item = selectedTreeItem.value || resolvedSelected.value;
  return item?.status || "active";
});

function parseCodeAndName(item) {
  if (!item) return { code: "", name: "" };
  const raw = String(item.name || "").trim();
  const fromName = raw.match(/^(#\S+)\s*-\s*(.*)$/);
  if (fromName) {
    return { code: fromName[1], name: fromName[2] || fromName[1] };
  }
  const codeRaw = item.code_str || (item.code != null && item.code !== "" ? `#${String(item.code).replace(/^#/, "")}` : "");
  const codePart = String(codeRaw).split(" ")[0] || "";
  return { code: codePart, name: raw || codePart };
}

function defaultExpanded(item) {
  if (item.expanded === true) return true;
  if (item.expanded === false) return false;
  return false;
}

function expandKeyForItem(itemId, itemType, parentId) {
  if (itemType === "role" && parentId != null && parentId !== "") {
    return `${parentId}-${itemId}`;
  }
  return itemId;
}

function setExpanded(key, value) {
  Vue.set(expandedItems, key, value);
}

function initializeExpandedItems(items, parentId = null) {
  (items || []).forEach((item) => {
    const key = expandKeyForItem(item.id, item.type, parentId);
    if (expandedItems[key] === undefined) {
      setExpanded(key, defaultExpanded(item));
    }
    if (item.children && item.children.length > 0) {
      initializeExpandedItems(item.children, item.id);
    }
  });
}

function findItemById(id, items) {
  const list = items || treeData.value;
  for (const item of list) {
    if (item.id === id || String(item.id) === String(id)) {
      return item;
    }
    if (item.children && item.children.length > 0) {
      const found = findItemById(id, item.children);
      if (found) return found;
    }
  }
  return null;
}

function hasNextSameType(items, currentIndex) {
  if (!items || currentIndex >= items.length - 1) {
    return false;
  }
  const currentType = items[currentIndex].type;
  for (let i = currentIndex + 1; i < items.length; i += 1) {
    if (items[i].type === currentType) {
      return true;
    }
  }
  return false;
}

function collapseSiblings(itemId, itemType, parentId) {
  if (itemType !== "role") return;
  const siblings = parentId
    ? findItemById(parentId, treeData.value)?.children || []
    : treeData.value;
  siblings.forEach((child) => {
    if (child.type === "role" && child.id !== itemId) {
      const key = expandKeyForItem(child.id, "role", parentId);
      setExpanded(key, false);
    }
  });
}

function handleToggle(itemId, itemType, parentId) {
  const expandKey = expandKeyForItem(itemId, itemType, parentId);
  const willBeExpanded = !expandedItems[expandKey];
  if (willBeExpanded) {
    collapseSiblings(itemId, itemType, parentId);
  }
  setExpanded(expandKey, willBeExpanded);
  emit("toggle-expand", {
    itemId,
    itemType,
    parentId,
    isExpanded: willBeExpanded,
  });
}

function handleItemSelected(itemData) {
  deletedItem.value = null;
  selectedItemInternal.value = itemData;
  emit("item-selected", itemData);
}

/**
 * ER-11676: a role/responsibility was deleted from the detail pane. Clear the selection and show the
 * shared EmptyState (no auto-select of a replacement) instead of navigating away.
 */
function handleItemDeleted(payload) {
  deletedItem.value = { type: payload?.type || "role" };
  selectedItemInternal.value = null;
  emit("item-deleted", payload);
}

function handleCardDrilldown(data) {
  emit("drilldown", data);
  const id = data?.id ?? data?.realId ?? data?.data?.id;
  const type = String(data?.type || data?.data?.type || "responsibility").toLowerCase();
  if (id == null) return;
  handleItemSelected({
    id,
    type,
    name: data?.name || data?.data?.name || data?.data?.code_str || "",
    data: data?.data || data,
  });
}

/**
 * ER-11851: RoleCard / ResponsibilityCard re-fetch their own details after a status
 * action and emit `roleDataUpdated` / `responsibilityDataUpdated`. Forward those events
 * up so the parent can refresh the tree rows (status-grey vs black) without a reload.
 */
function handleRoleDataUpdated(payload) {
  emit("role-data-updated", payload);
}

function handleResponsibilityDataUpdated(payload) {
  emit("responsibility-data-updated", payload);
}

function pickDefaultSelection(items) {
  if (!items || items.length === 0) return null;
  const firstRole = items.find((item) => item.type === "role");
  return firstRole || items[0];
}

watch(
  treeData,
  (items) => {
    initializeExpandedItems(items);
    if (props.selectedItem && props.selectedItem.id != null) return;
    if (selectedItemInternal.value) return;
    if (deletedItem.value) return;
    const next = pickDefaultSelection(items);
    if (next) {
      selectedItemInternal.value = {
        id: next.id,
        type: next.type,
        name: next.name,
      };
    }
  },
  { immediate: true }
);

watch(
  () => props.showDetailPanel,
  (show) => {
    if (show) {
      nextTick(() => attachRightPaneHeightObserver());
    } else {
      detachRightPaneHeightObserver();
      paneViewRightHeight.value = null;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.showDetailPanel) {
    nextTick(() => attachRightPaneHeightObserver());
  }
});

onBeforeUnmount(() => {
  detachRightPaneHeightObserver();
});

defineExpose({
  treeData,
  expandedItems,
  handleToggle,
  findItemById,
  hasNextSameType,
  resolvedSelected,
  paneLeftWidthPercent,
  handlePaneResizeStart,
});
</script>

<style scoped>
.direct-prr-tree-view {
  display: flex;
  flex-direction: column;
  min-width: 150px;
  min-height: 0;
  width: 100%;
  height: auto;
  max-height: none;
  overflow: visible;
  direction: ltr;
}

.direct-prr-tree-view:not(.direct-prr-tree-view--split) {
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.direct-prr-tree-view--split {
  flex-direction: row;
  align-items: flex-start;
  gap: 0;
  width: 100%;
  height: auto;
  max-height: none;
  overflow: visible;
}

.direct-prr-tree-view__tree {
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: #fcfcfd;
  padding: 16px 0;
  position: sticky;
  top: 0;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  width: 33%;
  flex: 0 0 auto;
  border: 1px solid #d1caca;
  box-sizing: border-box;
}

.direct-prr-tree-view:not(.direct-prr-tree-view--split) .direct-prr-tree-view__tree {
  width: 100%;
  flex: 1 1 auto;
  border: none;
  position: relative;
}

.direct-prr-tree-view__tree-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
}

.direct-prr-tree-view__tree--has-content .direct-prr-tree-view__tree-scroll {
  overflow-y: scroll;
}

.direct-prr-tree-view__tree--empty .direct-prr-tree-view__tree-scroll {
  overflow-y: hidden;
}

.direct-prr-tree-view__tree-scroll::-webkit-scrollbar {
  width: 10px;
  height: 0;
}

.direct-prr-tree-view__tree-scroll::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.direct-prr-tree-view__tree-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.direct-prr-tree-view__tree-scroll::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

.direct-prr-tree-view__tree-scroll::-webkit-scrollbar-corner {
  background: transparent;
  display: none;
}

.direct-prr-tree-view__resize {
  flex: 0 0 8px;
  width: 8px;
  min-height: 0;
  cursor: col-resize;
  background: transparent;
  align-self: stretch;
  position: relative;
}

.direct-prr-tree-view__resize:hover,
.direct-prr-tree-view__resize:active {
  background: rgba(0, 0, 0, 0.06);
}

.direct-prr-tree-view__resize::after {
  content: "";
  position: absolute;
  left: 3px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 40px;
  border-radius: 1px;
  background: #d1caca;
  opacity: 0.6;
}

.direct-prr-tree-view__resize:hover::after {
  opacity: 1;
  background: #9ca3af;
}

.direct-prr-tree-view__detail {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: auto;
  overflow-x: hidden;
  overflow-y: visible;
  background: #fff;
}

/*
  Nested RoleCard / ResponsibilityCard tables (EditCheckpointsTable, ChecklistCriteriaTable,
  CheckpointsTable) size themselves to the viewport (e.g. height: calc(100vh - 250px)).
  That pads this pane with empty space; the left tree then copies that height.
  Force those grids to hug their rows in this pane only.
*/
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.dx-datagrid),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.edit-checkpoints-table-wrapper .dx-datagrid),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.checklist-table-wrapper .dx-datagrid),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.checkpoint-table-wrapper .dx-datagrid) {
  height: auto !important;
  max-height: none !important;
}

.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.dx-datagrid-rowsview),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.edit-checkpoints-table-wrapper .dx-datagrid-rowsview),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.checklist-table-wrapper .dx-datagrid-rowsview),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.checkpoint-table-wrapper .dx-datagrid-rowsview) {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}

.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.dx-scrollable-wrapper),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.dx-scrollable-container) {
  height: auto !important;
  max-height: none !important;
  min-height: 0;
  overflow-x: visible !important;
  overflow-y: visible !important;
}

.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.dx-scrollbar-vertical),
.direct-prr-tree-view--split .direct-prr-tree-view__detail :deep(.dx-scrollable-scrollbar-vertical) {
  display: none !important;
}

.direct-prr-detail-empty,
.empty-tree-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 200px;
}

.direct-prr-detail-empty p,
.empty-tree-state p {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.tree-skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px;
}

.tree-skeleton-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
}

.tree-skeleton-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: tree-skeleton-pulse 1.2s ease-in-out infinite;
}

.tree-skeleton-content {
  flex: 1;
  min-width: 0;
}

.tree-skeleton-title {
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: tree-skeleton-pulse 1.2s ease-in-out infinite;
  max-width: 180px;
}

@keyframes tree-skeleton-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
